import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FaTimes,
  FaPaperPlane,
  FaCoins,
  FaPlus,
  FaShoppingCart,
  FaCheck,
  FaArrowLeft,
  FaTrash,
  FaHistory,
  FaSms,
  FaMobileAlt,
  FaEdit,
  FaInfoCircle,
  FaUpload,
} from 'react-icons/fa';
import api from '../../../services/api';
import CardPaymentForm from './CardPaymentForm';
import * as XLSX from 'xlsx';

const SmsModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('send'); // 'send', 'buy', 'history', 'payment'
  
  // Infos SMS
  const [tokens, setTokens] = useState(0);
  const [senderIds, setSenderIds] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [storeName, setStoreName] = useState('');
  
  // Formulaire d'envoi
  const [phones, setPhones] = useState([]); // Tableau de numéros
  const [phoneInput, setPhoneInput] = useState(''); // Input temporaire pour ajouter un numéro
  const [message, setMessage] = useState('');
  const [selectedSenderId, setSelectedSenderId] = useState('');
  const [tokensNeeded, setTokensNeeded] = useState(1);
  const [charCount, setCharCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [showAllPhones, setShowAllPhones] = useState(false); // Pour afficher tous les numéros ou seulement 20
  
  // Ajout nom d'expéditeur
  const [showAddSender, setShowAddSender] = useState(false);
  const [newSenderName, setNewSenderName] = useState('');
  
  // Packs et achat
  const [tokenPacks, setTokenPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  
  // Historique
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Messages
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Gestion des templates
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateContent, setTemplateContent] = useState('');

  // Charger les infos SMS au montage
  useEffect(() => {
    if (isOpen) {
      loadSmsInfo();
      
      // Vérifier si on revient d'un paiement Stripe
      const urlParams = new URLSearchParams(window.location.search);
      const smsPurchase = urlParams.get('sms_purchase');
      const sessionId = urlParams.get('session_id');
      
      if (smsPurchase === 'success' && sessionId) {
        handleVerifyPayment(sessionId);
        window.history.replaceState({}, '', window.location.pathname);
      }
    } else {
      // Réinitialiser l'affichage et la liste quand le modal se ferme
      setShowAllPhones(false);
    }
  }, [isOpen]);

  // Réinitialiser l'affichage si le nombre de numéros passe sous 20
  useEffect(() => {
    if (phones.length <= 20) {
      setShowAllPhones(false);
    }
  }, [phones.length]);

  // Charger les packs quand on passe à la vue achat
  useEffect(() => {
    if (view === 'buy' && tokenPacks.length === 0) {
      loadTokenPacks();
    }
    if (view === 'history') {
      loadHistory();
    }
  }, [view]);

  // Ref pour le debounce
  const debounceTimer = useRef(null);
  const fileInputRef = useRef(null);

  // Calculer le coût quand le message change (avec debounce)
  useEffect(() => {
    // Mettre à jour le compteur de caractères immédiatement
    setCharCount(message.length);
    
    // Calcul local du nombre de SMS (approximatif)
    if (message.length === 0) {
      setTokensNeeded(1);
      return;
    }
    
    // Calcul local approximatif (160 caractères par SMS, retour à la ligne = 1 caractère)
    const localTokens = message.length <= 160 ? 1 : Math.ceil(message.length / 160);
    setTokensNeeded(localTokens);
    
    // Annuler le timer précédent
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // Debounce de 500ms pour l'appel API
    debounceTimer.current = setTimeout(() => {
      if (message.length > 0) {
        calculateCost();
      }
    }, 500);
    
    // Cleanup
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [message]);

  const loadSmsInfo = async () => {
    try {
      setLoading(true);
      const response = await api.get('/store-sms/info');
      // Plus de "tokens" : facturation à l'usage. On garde la variable pour
      // ne pas casser les conditions d'envoi : on la fixe à un grand nombre
      // pour que les anciens checks de solde passent toujours.
      setTokens(Number.MAX_SAFE_INTEGER);
      setTemplates(response.data.templates || []);
      setStoreName(response.data.storeName || response.data.store_name || '');
      
      // Normaliser les sender IDs (snake_case vers camelCase)
      const rawSenderIds = response.data.senderIds || response.data.sender_ids || [];
      const normalizedSenderIds = rawSenderIds.map(s => ({
        id: s.id,
        senderId: s.sender_id || s.senderId,
        label: s.label,
        isDefault: s.is_default || s.isDefault,
      }));
      setSenderIds(normalizedSenderIds);
      
      // Sélectionner le sender ID par défaut
      const defaultSender = normalizedSenderIds.find(s => s.isDefault);
      if (defaultSender) {
        setSelectedSenderId(defaultSender.senderId);
      } else if (normalizedSenderIds.length > 0) {
        setSelectedSenderId(normalizedSenderIds[0].senderId);
      }
    } catch (err) {
      console.error('Erreur chargement SMS info:', err);
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadTokenPacks = async () => {
    try {
      setLoadingPacks(true);
      const response = await api.get('/store-sms/token-packs');
      setTokenPacks(response.data);
    } catch (err) {
      console.error('Erreur chargement packs:', err);
    } finally {
      setLoadingPacks(false);
    }
  };

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await api.get('/store-sms/history');
      setHistory(response.data.data || []);
    } catch (err) {
      console.error('Erreur chargement historique:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const calculateCost = async () => {
    try {
      const response = await api.post('/store-sms/calculate-cost', { message });
      setTokensNeeded(response.data.tokensNeeded);
      setCharCount(response.data.charCount);
    } catch (err) {
      console.error('Erreur calcul coût:', err);
    }
  };

  // Fonction pour normaliser un numéro de téléphone
  // Gère les formats : 0XXXXXXXXX, +33XXXXXXXXX, 0033XXXXXXXXX, 33XXXXXXXXX
  // Accepte uniquement les numéros commençant par 06 ou 07 et ayant exactement 10 caractères
  const normalizePhone = (phone) => {
    if (!phone) return null;
    
    // Nettoyer le numéro (enlever espaces, tirets, points, etc.)
    let cleaned = phone.toString().replace(/[\s\-\.\(\)"']/g, '');
    
    // Si le numéro commence déjà par 0 et a exactement 10 chiffres, vérifier qu'il commence par 06 ou 07
    if (cleaned.startsWith('0') && cleaned.length === 10 && /^\d{10}$/.test(cleaned)) {
      // Accepter uniquement les numéros commençant par 06 ou 07
      if (cleaned.startsWith('06') || cleaned.startsWith('07')) {
        return cleaned;
      }
      return null;
    }
    
    // Si le numéro commence par +33, remplacer par 0
    if (cleaned.startsWith('+33')) {
      cleaned = '0' + cleaned.substring(3);
    }
    // Si le numéro commence par 0033, remplacer par 0
    else if (cleaned.startsWith('0033')) {
      cleaned = '0' + cleaned.substring(4);
    }
    // Si le numéro commence par 33 (sans + ni 00), remplacer par 0
    else if (cleaned.startsWith('33') && cleaned.length > 2) {
      cleaned = '0' + cleaned.substring(2);
    }
    
    // Vérifier que c'est un numéro français valide (EXACTEMENT 10 chiffres, commence par 0)
    // ET qu'il commence par 06 ou 07
    if (cleaned.length === 10 && cleaned.startsWith('0') && /^\d{10}$/.test(cleaned)) {
      // Accepter uniquement les numéros commençant par 06 ou 07
      if (cleaned.startsWith('06') || cleaned.startsWith('07')) {
        return cleaned;
      }
    }
    
    return null;
  };

  const handleAddPhone = () => {
    const normalizedPhone = normalizePhone(phoneInput);
    
    if (!normalizedPhone) {
      setError('Numéro invalide. Seuls les numéros commençant par 06 ou 07 avec exactement 10 chiffres sont acceptés.');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    // Vérifier si le numéro n'est pas déjà dans la liste
    if (phones.includes(normalizedPhone)) {
      setError('Ce numéro est déjà dans la liste');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    setPhones([...phones, normalizedPhone]);
    setPhoneInput('');
    setError('');
  };

  const handleRemovePhone = (phoneToRemove) => {
    setPhones(phones.filter(p => p !== phoneToRemove));
  };

  // Fonction pour fermer la modale et réinitialiser l'affichage
  const handleClose = () => {
    setShowAllPhones(false);
    onClose();
  };

  // Fonction pour parser un fichier et extraire les numéros
  // Retourne { valid: [...], rejected: number }
  const parseFile = async (file) => {
    return new Promise((resolve, reject) => {
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.split('.').pop();
      
      // Pour les fichiers XLSX, utiliser readAsArrayBuffer
      if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            
            let numbers = [];
            let rejectedCount = 0;
            
            // Parcourir toutes les feuilles du workbook
            workbook.SheetNames.forEach((sheetName) => {
              const worksheet = workbook.Sheets[sheetName];
              // Convertir la feuille en JSON pour faciliter le parsing
              const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
              
              // Parcourir toutes les lignes et colonnes
              jsonData.forEach((row) => {
                if (Array.isArray(row)) {
                  row.forEach((cell) => {
                    if (cell !== null && cell !== undefined && cell !== '') {
                      const normalized = normalizePhone(String(cell).trim());
                      if (normalized) {
                        numbers.push(normalized);
                      } else {
                        rejectedCount++;
                      }
                    }
                  });
                }
              });
            });
            
            // Supprimer les doublons
            numbers = [...new Set(numbers)];
            resolve({ valid: numbers, rejected: rejectedCount });
          } catch (error) {
            reject(new Error('Erreur lors du parsing du fichier Excel: ' + error.message));
          }
        };
        
        reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier'));
        reader.readAsArrayBuffer(file);
        return;
      }
      
      // Pour les autres formats (CSV, JSON, TXT), utiliser readAsText
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          let numbers = [];
          let rejectedCount = 0;
          
          // Parser selon le format
          if (fileExtension === 'csv') {
            // Parser CSV
            const lines = content.split('\n');
            for (const line of lines) {
              // Séparer par virgule, point-virgule ou tabulation
              const values = line.split(/[,;\t]/);
              for (const value of values) {
                const normalized = normalizePhone(value.trim());
                if (normalized) {
                  numbers.push(normalized);
                } else if (value.trim()) {
                  rejectedCount++;
                }
              }
            }
          } else if (fileExtension === 'json') {
            // Parser JSON
            const data = JSON.parse(content);
            const extractNumbers = (obj) => {
              if (typeof obj === 'string') {
                const normalized = normalizePhone(obj);
                if (normalized) {
                  numbers.push(normalized);
                } else if (obj.trim()) {
                  rejectedCount++;
                }
              } else if (Array.isArray(obj)) {
                obj.forEach(extractNumbers);
              } else if (typeof obj === 'object' && obj !== null) {
                Object.values(obj).forEach(extractNumbers);
              }
            };
            extractNumbers(data);
          } else {
            // Parser TXT ou autres formats (ligne par ligne)
            const lines = content.split(/\r?\n/);
            for (const line of lines) {
              const trimmed = line.trim();
              if (trimmed) {
                const normalized = normalizePhone(trimmed);
                if (normalized) {
                  numbers.push(normalized);
                } else {
                  rejectedCount++;
                }
              }
            }
          }
          
          // Supprimer les doublons
          numbers = [...new Set(numbers)];
          resolve({ valid: numbers, rejected: rejectedCount });
        } catch (error) {
          reject(new Error('Erreur lors du parsing du fichier: ' + error.message));
        }
      };
      
      reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier'));
      reader.readAsText(file);
    });
  };

  // Gérer l'import de fichier
  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Vérifier le type de fichier
    const fileName = file.name.toLowerCase();
    const allowedExtensions = ['csv', 'json', 'txt', 'xlsx', 'xls'];
    const fileExtension = fileName.split('.').pop();
    
    if (!allowedExtensions.includes(fileExtension)) {
      setError('Format de fichier non supporté. Formats acceptés: CSV, JSON, TXT, XLSX');
      setTimeout(() => setError(''), 5000);
      return;
    }
    
    try {
      setError('');
      const { valid: importedNumbers, rejected: rejectedCount } = await parseFile(file);
      
      if (importedNumbers.length === 0) {
        setError('Aucun numéro valide trouvé dans le fichier. Seuls les numéros commençant par 06 ou 07 sont acceptés.');
        setTimeout(() => setError(''), 5000);
        return;
      }
      
      // Ajouter les numéros qui ne sont pas déjà dans la liste (pas de doublons)
      const newNumbers = importedNumbers.filter(num => !phones.includes(num));
      const duplicates = importedNumbers.length - newNumbers.length;
      
      if (newNumbers.length > 0) {
        setPhones([...phones, ...newNumbers]);
        let message = `${newNumbers.length} numéro${newNumbers.length > 1 ? 's' : ''} importé${newNumbers.length > 1 ? 's' : ''}`;
        if (duplicates > 0) {
          message += ` (${duplicates} doublon${duplicates > 1 ? 's' : ''} ignoré${duplicates > 1 ? 's' : ''})`;
        }
        if (rejectedCount > 0) {
          message += ` (${rejectedCount} numéro${rejectedCount > 1 ? 's' : ''} rejeté${rejectedCount > 1 ? 's' : ''} - seuls les 06 et 07 sont acceptés)`;
        }
        setSuccess(message);
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError('Tous les numéros valides du fichier sont déjà dans la liste');
        setTimeout(() => setError(''), 5000);
      }
      
      // Réinitialiser l'input file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      setError(error.message || 'Erreur lors de l\'import du fichier');
      setTimeout(() => setError(''), 5000);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSend = async () => {
    if (phones.length === 0 || !message || !selectedSenderId) {
      setError('Veuillez ajouter au moins un numéro et remplir le message');
      return;
    }

    // Plus de vérification de solde : facturation à l'usage 0,07 € HT par SMS,
    // payable en fin de mois sur la facture mensuelle.

    try {
      setSending(true);
      setError('');

      const response = await api.post('/store-sms/send', {
        phones, // Envoyer un tableau de numéros
        message,
        senderId: selectedSenderId,
      });

      setSuccess(`SMS envoyé avec succès à ${response.data.sentCount || phones.length} destinataire(s) !`);
      setPhones([]);
      setMessage('');

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleAddSenderId = async () => {
    if (!newSenderName) return;

    try {
      const response = await api.post('/store-sms/sender-ids', {
        senderId: newSenderName,
        label: newSenderName,
        isDefault: senderIds.length === 0,
      });

      const newSender = response.data.sender || response.data;
      // Normaliser les propriétés (snake_case vers camelCase)
      const normalizedSender = {
        id: newSender.id,
        senderId: newSender.sender_id || newSender.senderId || newSenderName,
        label: newSender.label || newSenderName,
        isDefault: newSender.is_default || newSender.isDefault || senderIds.length === 0,
      };

      setSenderIds([...senderIds, normalizedSender]);
      if (senderIds.length === 0) {
        setSelectedSenderId(normalizedSender.senderId);
      }
      setNewSenderName('');
      setShowAddSender(false);
    } catch (err) {
      setError('Erreur lors de l\'ajout du nom d\'expéditeur');
    }
  };

  const handleDeleteSenderId = async (id) => {
    try {
      await api.delete(`/store-sms/sender-ids/${id}`);
      setSenderIds(senderIds.filter(s => s.id !== id));
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const handleBuyPack = (pack) => {
    setSelectedPack(pack);
    setView('payment');
  };

  const handlePaymentSuccess = (newTokens, tokensAdded) => {
    setTokens(newTokens);
    setSuccess(`${tokensAdded} SMS ajoutés à votre compte !`);
    setSelectedPack(null);
    setView('send');
    setTimeout(() => setSuccess(''), 5000);
  };

  const handlePaymentCancel = () => {
    setSelectedPack(null);
    setView('buy');
  };

  const handleVerifyPayment = async (sessionId) => {
    try {
      const response = await api.post('/store-sms/verify-payment', { sessionId });
      if (response.data.success) {
        setTokens(response.data.tokens);
        setSuccess(`${response.data.tokensAdded || 0} SMS ajoutés à votre compte !`);
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('Erreur vérification paiement:', err);
    }
  };

  const handleUseTemplate = (template) => {
    // Remplacer les variables
    let content = template.content;
    content = content.replace(/{boutique}/g, storeName);
    setMessage(content);
  };

  const handleCreateTemplate = async () => {
    if (!templateName || !templateContent) {
      setError('Veuillez remplir le nom et le contenu du modèle');
      return;
    }

    try {
      const response = await api.post('/store-sms/templates', {
        name: templateName,
        content: templateContent,
      });

      const newTemplate = response.data.template || response.data;
      setTemplates([...templates, newTemplate]);
      setTemplateName('');
      setTemplateContent('');
      setShowTemplateForm(false);
      setSuccess('Modèle créé avec succès !');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erreur lors de la création du modèle');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!templateName || !templateContent || !editingTemplate) {
      setError('Veuillez remplir le nom et le contenu du modèle');
      return;
    }

    try {
      const response = await api.put(`/store-sms/templates/${editingTemplate.id}`, {
        name: templateName,
        content: templateContent,
      });

      const updatedTemplate = response.data.template || response.data;
      setTemplates(templates.map(t => t.id === editingTemplate.id ? updatedTemplate : t));
      setTemplateName('');
      setTemplateContent('');
      setEditingTemplate(null);
      setShowTemplateForm(false);
      setSuccess('Modèle modifié avec succès !');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erreur lors de la modification du modèle');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Supprimer ce modèle ?')) return;

    try {
      await api.delete(`/store-sms/templates/${templateId}`);
      setTemplates(templates.filter(t => t.id !== templateId));
      setSuccess('Modèle supprimé');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erreur lors de la suppression du modèle');
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateContent(template.content);
    setShowTemplateForm(true);
  };

  const closeTemplateForm = () => {
    setShowTemplateForm(false);
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateContent('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-[95vw] xl:max-w-screen-3xl h-[85vh] flex flex-col overflow-hidden shadow-2xl animate-fadeIn">
        {/* Header */}
        <div className="bg-fideliz-blue-bg border-b border-gray-100 px-5 py-4 flex-shrink-0">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
              {(view !== 'send') && (
                <button
                  onClick={() => {
                    if (view === 'payment') {
                      setView('buy');
                      setSelectedPack(null);
                    } else {
                      setView('send');
                    }
                  }}
                  className="p-2 hover:bg-white rounded-lg transition-colors text-fideliz-dark"
                >
                  <FaArrowLeft className="w-4 h-4" />
                </button>
              )}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-fideliz-blue rounded-xl flex items-center justify-center">
                  <FaSms className="text-white w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-fideliz-dark">
                    {view === 'send' && 'Envoyer un SMS'}
                    {view === 'buy' && 'Acheter des SMS'}
                    {view === 'history' && 'Historique des SMS'}
                    {view === 'payment' && 'Paiement sécurisé'}
                  </h2>
                  <p className="text-xs text-gray-500">
                    {view === 'send' && 'Envoyez des SMS personnalisés à vos clients'}
                    {view === 'buy' && 'Rechargez votre solde de SMS'}
                    {view === 'history' && 'Consultez l\'historique de vos envois'}
                    {view === 'payment' && 'Finalisez votre achat'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Tarification à l'usage */}
              <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-gray-100">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <FaCoins className="text-orange-500 w-4 h-4" />
                </div>
                <div>
                  <p className="text-[0.625rem] text-gray-500 font-medium leading-none">Facturé à l'usage</p>
                  <p className="text-base font-bold text-fideliz-dark">0,07 € <span className="text-xs font-medium text-gray-500">HT / SMS</span></p>
                </div>
              </div>

              <button
                onClick={handleClose} 
                className="p-2 hover:bg-white rounded-lg transition-colors text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Navigation par onglets */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setView('send')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-1.5 ${
                view === 'send' 
                  ? 'bg-fideliz-blue text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <FaPaperPlane className="w-3.5 h-3.5" />
              Envoyer
            </button>
            <button
              onClick={() => setView('history')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-1.5 ${
                view === 'history' 
                  ? 'bg-fideliz-blue text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <FaHistory className="w-3.5 h-3.5" />
              Historique
            </button>
          </div>
        </div>

        {/* Contenu */}
        <div className="p-5 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-10 h-10 border-3 border-fideliz-blue border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-3 text-sm text-gray-500">Chargement...</p>
            </div>
          ) : (
            <>
              {/* Messages d'alerte */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
                  <FaTimes className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}
              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
                  <FaCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <p className="text-sm">{success}</p>
                </div>
              )}

              {/* Vue Envoi */}
              {view === 'send' && (
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                  {/* Formulaire principal */}
                  <div className="lg:col-span-3 space-y-4">
                    {/* Nom d'expéditeur */}
                    <div>
                      <label className="block text-sm font-medium text-fideliz-dark mb-1.5">
                        Nom d'expéditeur
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={selectedSenderId}
                          onChange={(e) => setSelectedSenderId(e.target.value)}
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-fideliz-blue/20 focus:border-fideliz-blue transition-all bg-white"
                        >
                          {senderIds.length === 0 && (
                            <option value="">Ajoutez un nom d'expéditeur</option>
                          )}
                          {senderIds.map((s) => (
                            <option key={s.id} value={s.senderId}>
                              {s.senderId}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => setShowAddSender(!showAddSender)}
                          className="bg-fideliz-blue-bg text-fideliz-blue px-3 py-2.5 rounded-lg hover:bg-fideliz-blue hover:text-white transition-all"
                        >
                          <FaPlus className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Ajout nom d'expéditeur */}
                      {showAddSender && (
                        <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                          <input
                            type="text"
                            placeholder="Nom d'expéditeur (max 11 caractères)"
                            value={newSenderName}
                            onChange={(e) => setNewSenderName(e.target.value.substring(0, 11))}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-fideliz-blue/20 focus:border-fideliz-blue transition-all"
                          />
                          <p className="text-xs text-gray-500">
                            Ce nom apparaîtra comme expéditeur sur le téléphone de vos clients
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={handleAddSenderId}
                              className="flex-1 bg-fideliz-blue text-white py-2 rounded-lg text-sm font-medium hover:bg-fideliz-blue hover:opacity-90 transition-colors"
                            >
                              Ajouter
                            </button>
                            <button
                              onClick={() => setShowAddSender(false)}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Numéros de téléphone */}
                    <div>
                      <label className="block text-sm font-medium text-fideliz-dark mb-1.5">
                        Numéro(s) de téléphone
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2">
                            <FaMobileAlt className="w-4 h-4 text-gray-400" />
                          </div>
                          <input
                            type="tel"
                            value={phoneInput}
                            onChange={(e) => setPhoneInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddPhone();
                              }
                            }}
                            placeholder="06 12 34 56 78"
                            className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-fideliz-blue/20 focus:border-fideliz-blue transition-all"
                          />
                        </div>
                        <button
                          onClick={handleAddPhone}
                          disabled={!phoneInput.trim()}
                          className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-1.5 ${
                            !phoneInput.trim()
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-fideliz-blue text-white hover:bg-fideliz-blue hover:opacity-90'
                          }`}
                        >
                          <FaPlus className="w-3.5 h-3.5" />
                          Ajouter
                        </button>
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          type="button"
                          className="px-4 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                          title="Importer depuis un fichier (CSV, JSON, TXT)"
                        >
                          <FaUpload className="w-3.5 h-3.5" />
                          Importer
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".csv,.json,.txt,.xlsx,.xls"
                          onChange={handleImportFile}
                          className="hidden"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1.5">
                        Formats acceptés: CSV, JSON, TXT, XLSX. Seuls les numéros commençant par 06 ou 07 sont acceptés.
                      </p>
                      
                      {/* Liste des numéros ajoutés */}
                      {phones.length > 0 && (
                        <div className="mt-2">
                          <div className="flex flex-wrap gap-2">
                            {(showAllPhones ? phones : phones.slice(0, 20)).map((phone, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2 bg-fideliz-blue-bg text-fideliz-blue px-3 py-1.5 rounded-lg text-sm"
                              >
                                <span>{phone}</span>
                                <button
                                  onClick={() => handleRemovePhone(phone)}
                                  className="text-fideliz-blue hover:text-red-500 transition-colors"
                                >
                                  <FaTimes className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                          {phones.length > 20 && !showAllPhones && (
                            <button
                              onClick={() => setShowAllPhones(true)}
                              className="mt-2 text-sm text-fideliz-blue hover:text-fideliz-blue font-medium"
                            >
                              Voir tous les {phones.length} numéros
                            </button>
                          )}
                          {phones.length > 20 && showAllPhones && (
                            <button
                              onClick={() => setShowAllPhones(false)}
                              className="mt-2 text-sm text-fideliz-blue hover:text-fideliz-blue font-medium"
                            >
                              Réduire
                            </button>
                          )}
                        </div>
                      )}
                      
                      {phones.length > 0 && (
                        <p className="text-xs text-gray-500 mt-2">
                          {phones.length} numéro{phones.length > 1 ? 's' : ''} ajouté{phones.length > 1 ? 's' : ''} • Coût total: {tokensNeeded * phones.length} SMS
                        </p>
                      )}
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-medium text-fideliz-dark mb-1.5">
                        Message
                      </label>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Écrivez votre message personnalisé..."
                        rows={5}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-fideliz-blue/20 focus:border-fideliz-blue transition-all resize-none"
                      />
                      <div className="flex items-center justify-between mt-2 bg-gray-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-1.5 h-1.5 rounded-full ${charCount <= 160 ? 'bg-green-500' : charCount <= 320 ? 'bg-amber-500' : 'bg-red-500'}`}></div>
                          <span className="text-xs text-gray-600">
                            <span className="font-semibold">{charCount}</span> / 160 caractères
                          </span>
                        </div>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${tokensNeeded === 1 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {tokensNeeded} SMS
                        </span>
                      </div>
                      {charCount > 160 && (
                        <p className="text-xs text-amber-600 mt-1.5">
                          💡 Votre message dépasse 160 caractères, il utilisera {tokensNeeded} SMS.
                        </p>
                      )}
                    </div>

                    {/* Bouton Envoyer */}
                    <button
                      onClick={handleSend}
                      disabled={sending || phones.length === 0 || !message || !selectedSenderId}
                      className={`w-full py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                        sending || phones.length === 0 || !message || !selectedSenderId
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-fideliz-blue text-white hover:bg-fideliz-blue hover:opacity-90'
                      }`}
                    >
                      {sending ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <FaPaperPlane className="w-4 h-4" /> 
                          Envoyer à {phones.length} destinataire{phones.length > 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                    
                    {phones.length > 0 && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center gap-2">
                        <FaInfoCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                        <p className="text-orange-700 text-xs">
                          {phones.length * tokensNeeded} SMS facturé{phones.length * tokensNeeded > 1 ? 's' : ''} à <strong>0,07 € HT</strong> chacun, ajouté à votre facture mensuelle.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Panneau latéral - Templates */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Templates prédéfinis */}
                    <div className="bg-fideliz-purple rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-fideliz-blue/10 rounded-lg flex items-center justify-center">
                            <FaEdit className="w-4 h-4 text-fideliz-blue" />
                          </div>
                          <h3 className="font-semibold text-sm text-fideliz-dark">Mes modèles</h3>
                        </div>
                        {!showTemplateForm && (
                          <button
                            onClick={() => setShowTemplateForm(true)}
                            className="text-fideliz-blue hover:bg-fideliz-blue hover:text-white p-1.5 rounded-lg transition-all"
                            title="Créer un modèle"
                          >
                            <FaPlus className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      
                      {/* Formulaire création/édition */}
                      {showTemplateForm && (
                        <div className="bg-white rounded-lg p-3 mb-3 space-y-2">
                          <input
                            type="text"
                            placeholder="Nom du modèle"
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-fideliz-blue/20 focus:border-fideliz-blue"
                          />
                          <textarea
                            placeholder="Contenu du message..."
                            value={templateContent}
                            onChange={(e) => setTemplateContent(e.target.value)}
                            rows={3}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-fideliz-blue/20 focus:border-fideliz-blue resize-none"
                          />
                          <p className="text-[0.625rem] text-gray-500">
                            Utilisez {'{boutique}'} pour insérer le nom de votre boutique
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}
                              className="flex-1 bg-fideliz-blue text-white py-1.5 rounded-lg text-xs font-medium hover:bg-fideliz-blue hover:opacity-90 transition-colors"
                            >
                              {editingTemplate ? 'Modifier' : 'Créer'}
                            </button>
                            <button
                              onClick={closeTemplateForm}
                              className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-300 transition-colors"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Liste des templates */}
                      {templates.length > 0 ? (
                        <div className="space-y-1.5">
                          {templates.map((t) => (
                            <div
                              key={t.id}
                              className="group flex items-center gap-2 bg-white rounded-lg border border-gray-100 overflow-hidden"
                            >
                              <button
                                onClick={() => handleUseTemplate(t)}
                                className="flex-1 text-left px-3 py-2 text-gray-700 hover:bg-fideliz-blue hover:text-white text-xs transition-all"
                              >
                                {t.name}
                              </button>
                              <div className="flex items-center gap-0.5 pr-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => openEditTemplate(t)}
                                  className="p-1.5 text-gray-400 hover:text-fideliz-blue hover:bg-gray-100 rounded transition-all"
                                  title="Modifier"
                                >
                                  <FaEdit className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleDeleteTemplate(t.id)}
                                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-all"
                                  title="Supprimer"
                                >
                                  <FaTrash className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : !showTemplateForm && (
                        <p className="text-xs text-gray-500 text-center py-3">
                          Aucun modèle créé
                        </p>
                      )}
                    </div>

                  </div>
                </div>
              )}

              {/* Vue Achat */}
              {view === 'buy' && (
                <div className="space-y-5">
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 bg-fideliz-blue-bg px-3 py-1.5 rounded-full">
                      <FaCoins className="w-3.5 h-3.5 text-amber-500" />
                      <span className="font-medium text-xs text-fideliz-dark">1 SMS = 160 caractères</span>
                    </div>
                  </div>
                  
                  {loadingPacks ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="w-10 h-10 border-3 border-fideliz-blue border-t-transparent rounded-full animate-spin"></div>
                      <p className="mt-3 text-sm text-gray-500">Chargement des offres...</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {tokenPacks.map((pack, index) => (
                        <div
                          key={pack.id}
                          className={`relative rounded-lg p-3 transition-all hover:shadow-md ${
                            pack.discount_percent > 0
                              ? 'bg-gradient-to-br from-fideliz-blue to-fideliz-blue text-white'
                              : 'bg-white border border-gray-200 hover:border-fideliz-blue'
                          }`}
                        >
                          {pack.discount_percent > 0 && (
                            <div className="absolute -top-1.5 -right-1.5 bg-green-500 text-white text-[0.625rem] font-bold px-1.5 py-0.5 rounded-full">
                              -{pack.discount_percent}%
                            </div>
                          )}
                          
                          <div className="text-center">
                            <h3 className={`font-medium text-xs mb-1.5 ${pack.discount_percent > 0 ? 'text-white' : 'text-fideliz-dark'}`}>
                              {pack.name}
                            </h3>
                            <div className="mb-2">
                              <div className={`flex items-center justify-center gap-1 ${pack.discount_percent > 0 ? 'text-white/70' : 'text-gray-500'}`}>
                                <FaCoins className={`w-3 h-3 ${pack.discount_percent > 0 ? 'text-yellow-300' : 'text-amber-500'}`} />
                                <span className={`text-lg font-bold ${pack.discount_percent > 0 ? 'text-white' : 'text-fideliz-blue'}`}>
                                  {pack.tokens}
                                </span>
                                <span className={`text-[0.625rem] ${pack.discount_percent > 0 ? 'text-white/60' : 'text-gray-500'}`}>
                                  SMS
                                </span>
                              </div>
                            </div>
                            
                            <div className="mb-2.5">
                              <p className={`text-base font-bold ${pack.discount_percent > 0 ? 'text-white' : 'text-fideliz-dark'}`}>
                                {parseFloat(pack.price_ht || pack.price).toFixed(2)} € <span className="text-[0.625rem] font-normal">HT</span>
                              </p>
                              <p className={`text-[0.625rem] ${pack.discount_percent > 0 ? 'text-white/60' : 'text-gray-400'}`}>
                                {(parseFloat(pack.price_ht || pack.price) / pack.tokens).toFixed(3)} €/SMS
                              </p>
                            </div>
                            
                            <button
                              onClick={() => handleBuyPack(pack)}
                              className={`w-full py-2 rounded-lg font-medium text-xs transition-all ${
                                pack.discount_percent > 0
                                  ? 'bg-white text-fideliz-blue hover:bg-gray-100'
                                  : 'bg-fideliz-blue text-white hover:bg-fideliz-blue hover:opacity-90'
                              }`}
                            >
                              Acheter
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Vue Historique */}
              {view === 'history' && (
                <div className="space-y-4">
                  {loadingHistory ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="w-10 h-10 border-3 border-fideliz-blue border-t-transparent rounded-full animate-spin"></div>
                      <p className="mt-3 text-sm text-gray-500">Chargement de l'historique...</p>
                    </div>
                  ) : history.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <FaHistory className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm font-medium text-gray-700">Aucun SMS envoyé</p>
                      <p className="text-xs text-gray-500 mt-1">Vos SMS envoyés apparaîtront ici</p>
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {history.map((item) => (
                        <div
                          key={item.id}
                          className={`rounded-lg p-4 border transition-all ${
                            item.status === 'sent' 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div className="flex items-start gap-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                item.status === 'sent' ? 'bg-green-100' : 'bg-red-100'
                              }`}>
                                {item.status === 'sent' ? (
                                  <FaCheck className="w-4 h-4 text-green-600" />
                                ) : (
                                  <FaTimes className="w-4 h-4 text-red-600" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-fideliz-dark">{item.phone}</p>
                                <p className="text-xs text-gray-600 mt-0.5 break-words">{item.content}</p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                item.status === 'sent' 
                                  ? 'bg-green-200 text-green-800' 
                                  : 'bg-red-200 text-red-800'
                              }`}>
                                {item.status === 'sent' ? 'Envoyé' : 'Échoué'}
                              </span>
                              <p className="text-gray-500 text-[0.625rem] mt-1">
                                {new Date(item.created_at || item.createdAt).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Vue Paiement */}
              {view === 'payment' && selectedPack && (
                <div className="max-w-md mx-auto">
                  <div className="bg-fideliz-blue-bg rounded-xl p-4 mb-6">
                    <h3 className="font-semibold text-sm text-fideliz-dark mb-3">Récapitulatif</h3>
                    <div className="flex items-center justify-between bg-white rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                          <FaCoins className="w-4 h-4 text-amber-500" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm text-fideliz-dark">{selectedPack.name}</p>
                          <p className="text-xs text-gray-500">{selectedPack.tokens} SMS</p>
                        </div>
                      </div>
                      <p className="text-lg font-bold text-fideliz-blue">
                        {parseFloat(selectedPack.price_ttc || selectedPack.price * 1.2).toFixed(2)} €
                      </p>
                    </div>
                  </div>
                  
                  <CardPaymentForm
                    pack={selectedPack}
                    onSuccess={handlePaymentSuccess}
                    onCancel={handlePaymentCancel}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmsModal;
